import { MoreOutlined } from '@ant-design/icons';
import { useDispatch } from '@redux';
import { Button, Dropdown, Modal, Tabs } from 'antd';
import AddNewView from 'components/rest/CustomLayout/AddNewView';
import { CustomLayoutWrapper } from 'components/rest/CustomLayout/styles';
import {
  useActiveDefaultTab,
  // useActiveTab,
  useGetRootPath,
  useSaveFilter,
} from 'hooks/useShowLayout';
import { MouseEvent, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TLayoutItem, delLayout } from '../../../@redux/layouts/actions';
import RenameModal from 'components/rest/CustomLayout/RenameModal';
import { removeEmptyValueOfPathUrl } from 'utils/tools';

interface ICustomLayoutProps {
  items: TLayoutItem[];
  hasSubCustomLayout?: React.ReactElement;
}

const MoreButton = ({
  item,
  setEditName,
}: {
  item: TLayoutItem;
  setEditName: (item: TLayoutItem) => void;
}) => {
  const { rootPath } = useGetRootPath();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const onRename = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setEditName(item);
  };
  const onDelete = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    Modal.confirm({
      content: 'Are you sure you want to delete this VIEW?',
      onOk: () => {
        dispatch(delLayout(item));
        navigate(rootPath);
      },
    });
  };
  if (item.title === 'All') return null;
  return (
    <Dropdown
      menu={{
        items: [
          {
            key: 'rename',
            label: <div onClick={onRename}>Rename</div>,
          },
          {
            key: 'delete',
            label: <div onClick={onDelete}>Delete</div>,
          },
        ],
      }}
    >
      <MoreOutlined className="mr-0 ml-2.5" />
    </Dropdown>
  );
};

export default function CustomLayout({
  items,
  hasSubCustomLayout,
}: ICustomLayoutProps) {
  const navigate = useNavigate();
  const { pathname, search } = useGetRootPath();
  // const defaultLayouts = useSelector<Layouts>(
  //   (state) => state.layouts.defaultLayouts,
  // );
  const ref = useRef<{ setOpen: (e: boolean) => void }>(null);
  const renameRef = useRef<{ setSelected: (e: TLayoutItem | null) => void }>(
    null,
  );

  // const activeTab = useActiveTab();
  const activeDefaultTab = useActiveDefaultTab(items);

  const saveFilters = useSaveFilter();

  const setEditName = (item: TLayoutItem) => {
    renameRef.current.setSelected(item);
  };

  const tabs = useMemo(() => {
    const _tmp = items.map((e, index) => ({
      key: e.title,
      label: e?.label || (
        <span>
          {e.title}
          {!e?.isDefault && <MoreButton setEditName={setEditName} item={e} />}
        </span>
      ),
      closable: !!index,
    }));
    _tmp.push({
      key: 'add',
      label: <span>+ View</span>,
      closable: false,
    });
    return _tmp;
  }, [items]);

  const onAdd = () => {
    ref.current.setOpen(true);
  };

  const item = useMemo(
    () => items.find((e) => e.title === activeDefaultTab),
    [items, activeDefaultTab],
  );

  const saveFilter = () => {
    const searchParams = new URLSearchParams(search);
    const filters = searchParams.get('filters');
    saveFilters(
      {
        q: searchParams.get('q'),
        filters: filters ? JSON.parse(filters.toString()) || [] : undefined,
        fieldSearch: searchParams.get('fieldSearch'),
      },
      item,
    );
  };

  const isShowSaveFilter = useMemo(() => {
    const pathNameFormat = removeEmptyValueOfPathUrl(pathname);
    const customPath = item?.customPath
      ? removeEmptyValueOfPathUrl(item?.customPath)
      : null;
    if (hasSubCustomLayout && item?.pathId !== 'ALL') {
      const searchParams = new URLSearchParams(search);
      if (searchParams.get('filters')) {
        return pathNameFormat !== customPath;
      }
      return searchParams.get('customLayout') !== item?.pathId;
    }
    return pathNameFormat !== customPath;
  }, [item, pathname, search]);

  const onChange = (activeKey) => {
    const activeItem = items.find((e) => e.title === activeKey);
    activeItem?.customPath && navigate(activeItem.customPath);
    if (activeKey === 'add') {
      onAdd();
    }
  };

  // useEffect(() => {
  //   const searchParams = new URLSearchParams();
  //   currentLayoutFilters?.length &&
  //     searchParams.set(
  //       'filters',
  //       JSON.stringify(currentLayoutFilters, null, 0),
  //     );
  //   if (
  //     activeTab === 'All' &&
  //     pathname === rootPath &&
  //     pathname !== `${rootPath}?${searchParams.toString()}`
  //   ) {
  //     navigate(`${rootPath}?${searchParams.toString()}`);
  //   }
  // }, [activeTab, pathname, currentLayoutFilters]);

  return (
    <>
      <CustomLayoutWrapper>
        <Tabs
          onChange={onChange}
          activeKey={activeDefaultTab}
          tabBarExtraContent={
            isShowSaveFilter ? (
              <Button onClick={saveFilter} className="btn-overlay">
                Save Filter
              </Button>
            ) : null
          }
          items={tabs}
        />
      </CustomLayoutWrapper>
      <AddNewView existLayouts={items} ref={ref} />
      <RenameModal existLayouts={items} ref={renameRef} />
    </>
  );
}
